import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { usePublicStats } from "../components/DynamicGrantCount";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Lightning, CheckCircle, Buildings, ChartLineUp, ShieldCheck, Handshake, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const KNOWN_REFERRERS = {
  "wardell-001": "Wardell",
};

const FOCUS_AREAS = [
  "Workforce Development", "Youth & Education", "Health & Wellness",
  "Environment & Conservation", "Arts & Culture", "Housing & Homelessness",
  "Veterans", "Faith-Based", "Community Development", "Economic Mobility",
];

const TIERS = [
  {
    id: "founding",
    name: "Founding Sponsor",
    headline: "Be the named partner.",
    description: "For companies that want to lead. Featured Corporate Scout placement, mission-match analytics across all SC nonprofits, and a branded sponsor page on FlashGrants.",
    bullets: [
      "Featured placement in Corporate Scout (every Premium nonprofit sees you first)",
      "Custom branded sponsor page on flash-grants.com",
      "Mission-match analytics across 74+ verified SC nonprofits",
      "Quarterly impact report — outcomes data from every grant funded",
      "Direct intro pipeline: nonprofits matching your CSR focus reach you first",
    ],
  },
  {
    id: "regional",
    name: "Regional Partner",
    headline: "Own your region.",
    description: "Multi-county exposure for companies operating across the Lowcountry, Midlands, Upstate, or Grand Strand. Quarterly outcomes reporting included.",
    bullets: [
      "Listed in Corporate Scout for your region's nonprofits",
      "Quarterly impact report tied to your geographic footprint",
      "Mission-match scoring against regional nonprofits",
      "Co-marketing opportunities with FlashGrants",
    ],
  },
  {
    id: "statewide",
    name: "Statewide Champion",
    headline: "Statewide visibility.",
    description: "Full South Carolina coverage for national franchises and statewide brands. Custom-branded campaigns and full Impact Dashboard access.",
    bullets: [
      "Statewide Corporate Scout listing across every county",
      "Custom-branded campaigns (e.g., \"Sponsored by [Brand]\")",
      "Full Impact Dashboard access — aggregate outcomes data",
      "Annual on-site or virtual partnership review",
    ],
  },
];

export default function ForCorporations() {
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").trim().toLowerCase();
  const refLabel = useMemo(() => KNOWN_REFERRERS[refCode] || "", [refCode]);

  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    title: "",
    email: "",
    phone: "",
    tier_interest: "",
    annual_budget: "",
    focus_areas: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const publicStats = usePublicStats();
  const scoutCount = publicStats?.corporate_scouts || 30;

  // Track this visit for referral attribution (fire-and-forget)
  useEffect(() => {
    axios.post(`${API}/corporate/track-visit`, { ref_code: refCode }).catch(() => {});
  }, [refCode]);

  const toggleFocus = (area) => {
    setForm((f) => ({
      ...f,
      focus_areas: f.focus_areas.includes(area)
        ? f.focus_areas.filter((a) => a !== area)
        : [...f.focus_areas, area],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error("Please fill in company, contact name, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/corporate/inquiry`, { ...form, ref_code: refCode });
      setSubmitted(true);
      toast.success("Got it. We'll reach out within 1 business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("inquiry-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="for-corporations-page">
      {/* Top accent bar */}
      <div className="h-1 bg-emerald-500" />

      {/* Header */}
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="header-home-link">
          <Lightning weight="fill" className="text-emerald-500 w-7 h-7" />
          <span className="text-xl font-bold">FlashGrants</span>
        </a>
        <a href="#inquiry-form" className="text-sm text-emerald-500 hover:text-emerald-400 font-semibold" data-testid="header-cta">
          Request a demo →
        </a>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-20">
        {refLabel && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-950/40 border border-emerald-800 text-emerald-400 text-sm mb-8" data-testid="referrer-badge">
            <Handshake weight="fill" className="w-4 h-4" />
            Warm intro via <span className="font-semibold">{refLabel}</span>
          </div>
        )}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-6">
          Fund the South Carolina<br />
          <span className="text-emerald-500">nonprofits already moving</span><br />
          the needle.
        </h1>
        <p className="text-xl md:text-2xl text-zinc-300 max-w-3xl leading-relaxed mb-10">
          FlashGrants is the platform 74+ verified SC nonprofits use to find funding. We're inviting a small group of corporate sponsors to fund them — directly, measurably, and with mission-match data instead of guesswork.
        </p>
        <div className="flex flex-wrap gap-4">
          <Button
            onClick={scrollToForm}
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-8 py-6 text-base rounded-md"
            data-testid="hero-request-demo-btn"
          >
            Request a partnership briefing <ArrowRight weight="bold" className="ml-2 w-5 h-5" />
          </Button>
          <Button
            variant="outline"
            onClick={() => document.getElementById("tiers")?.scrollIntoView({ behavior: "smooth" })}
            className="border-zinc-700 hover:border-emerald-500 hover:text-emerald-400 px-8 py-6 text-base"
            data-testid="hero-see-tiers-btn"
          >
            See sponsorship tiers
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-12 border-t border-zinc-800">
          <Stat number="74+" label="Verified SC nonprofits" />
          <Stat number={String(scoutCount)} label="Active corporate scouts" />
          <Stat number="$700K+" label="In tracked grant funding" />
          <Stat number="100%" label="Verified nonprofit profiles" />
        </div>
      </section>

      {/* The problem */}
      <section className="bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            Your CSR team gets <span className="text-emerald-500">200 grant requests a year.</span>
          </h2>
          <p className="text-xl text-zinc-400 max-w-3xl mb-12">
            Half are from organizations that don't match your mission. The rest are PDFs in inboxes. There's no scoring, no outcomes data, no way to compare impact across grants. FlashGrants fixes that.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <ProblemCard
              icon={<Buildings weight="duotone" className="w-8 h-8 text-emerald-500" />}
              title="Vetted, not random"
              body="Every nonprofit on FlashGrants has a verified profile, a documented mission, and active grant activity. No cold pitches from unknown orgs."
            />
            <ProblemCard
              icon={<ChartLineUp weight="duotone" className="w-8 h-8 text-emerald-500" />}
              title="Match by mission"
              body="Score nonprofits against your specific CSR focus areas — workforce, youth, environment, veterans. Top matches surface first."
            />
            <ProblemCard
              icon={<ShieldCheck weight="duotone" className="w-8 h-8 text-emerald-500" />}
              title="Outcomes you can audit"
              body="Aggregate funding milestones and outcomes for every dollar deployed."
            />
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section id="tiers" className="max-w-6xl mx-auto px-6 py-20">
        <div className="mb-12">
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            Three ways to <span className="text-emerald-500">show up.</span>
          </h2>
          <p className="text-xl text-zinc-400 max-w-3xl">
            Every partnership is custom. We'll talk through your CSR goals, your geographic footprint, and what success looks like — then build the right tier together.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {TIERS.map((tier) => (
            <Card key={tier.id} className="bg-zinc-950 border-zinc-800 hover:border-emerald-700 transition-all" data-testid={`tier-card-${tier.id}`}>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                <p className="text-emerald-500 font-semibold mb-4">{tier.headline}</p>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">{tier.description}</p>
                <ul className="space-y-3 mb-8">
                  {tier.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-6 border-t border-zinc-800">
                  <p className="text-2xl font-bold text-white mb-1">Contact us</p>
                  <p className="text-xs text-zinc-500">Custom pricing based on scope, geography, and term.</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Custom partnership menu */}
      <section className="bg-gradient-to-b from-black to-zinc-950 border-y border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-10 max-w-3xl">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              Every partnership is <span className="text-emerald-500">custom.</span>
            </h2>
            <p className="text-xl text-zinc-400 leading-relaxed">
              Founding Sponsors typically include 4&ndash;6 of the following &mdash; built around your CSR goals, your geographic footprint, and what success looks like for your team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-x-10 gap-y-4 mb-10" data-testid="custom-menu-list">
            {[
              "Co-branded sponsor page on flash-grants.com",
              "Custom referral & employee engagement tracking",
              "Quarterly Impact Reports — branded, automated",
              "Featured Corporate Scout placement",
              "Sponsored grant campaigns with your branding",
              "Direct warm intros to mission-matched nonprofits",
              "Custom geographic territory (county, region, statewide)",
              "Annual partnership review (on-site or virtual)",
              "Logo placement on FlashGrants homepage",
              "...and more, designed around your goals",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-zinc-900">
                <CheckCircle weight="fill" className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-1" />
                <span className="text-zinc-200 text-base">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-zinc-400 text-base italic max-w-2xl">
            Tell us about your CSR priorities below &mdash; we'll show you exactly what the partnership looks like for your company.
          </p>
        </div>
      </section>

      {/* Form */}
      <section id="inquiry-form" className="bg-zinc-950 border-t border-zinc-900">
        <div className="max-w-3xl mx-auto px-6 py-20">
          {submitted ? (
            <div className="text-center py-16" data-testid="inquiry-success">
              <CheckCircle weight="fill" className="w-20 h-20 text-emerald-500 mx-auto mb-6" />
              <h2 className="text-4xl font-black mb-4">Thank you.</h2>
              <p className="text-xl text-zinc-300 mb-8">
                We'll reach out within 1 business day to schedule a 30-minute partnership briefing.
              </p>
              <p className="text-sm text-zinc-500">
                In the meantime, take a look at the platform: <a href="https://flash-grants.com" className="text-emerald-500 hover:text-emerald-400">flash-grants.com</a>
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-4xl md:text-5xl font-black mb-4">
                Request a <span className="text-emerald-500">partnership briefing.</span>
              </h2>
              <p className="text-xl text-zinc-400 mb-10">
                30 minutes. We'll show you the platform, walk through real SC nonprofit profiles, and discuss the right partnership tier for your CSR goals.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Company *">
                    <Input
                      required
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="bg-black border-zinc-800 focus:border-emerald-500 h-12"
                      data-testid="input-company"
                    />
                  </Field>
                  <Field label="Your name *">
                    <Input
                      required
                      value={form.contact_name}
                      onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                      className="bg-black border-zinc-800 focus:border-emerald-500 h-12"
                      data-testid="input-contact-name"
                    />
                  </Field>
                  <Field label="Title">
                    <Input
                      placeholder="e.g. VP of CSR, Regional Director"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="bg-black border-zinc-800 focus:border-emerald-500 h-12"
                      data-testid="input-title"
                    />
                  </Field>
                  <Field label="Email *">
                    <Input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="bg-black border-zinc-800 focus:border-emerald-500 h-12"
                      data-testid="input-email"
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-black border-zinc-800 focus:border-emerald-500 h-12"
                      data-testid="input-phone"
                    />
                  </Field>
                  <Field label="Tier of interest">
                    <select
                      value={form.tier_interest}
                      onChange={(e) => setForm({ ...form, tier_interest: e.target.value })}
                      className="w-full h-12 bg-black border border-zinc-800 rounded-md px-3 focus:border-emerald-500 focus:outline-none text-white"
                      data-testid="select-tier"
                    >
                      <option value="">Not sure yet</option>
                      <option value="founding">Founding Sponsor</option>
                      <option value="regional">Regional Partner</option>
                      <option value="statewide">Statewide Champion</option>
                    </select>
                  </Field>
                </div>

                <Field label="Annual CSR / community giving budget (optional)">
                  <select
                    value={form.annual_budget}
                    onChange={(e) => setForm({ ...form, annual_budget: e.target.value })}
                    className="w-full h-12 bg-black border border-zinc-800 rounded-md px-3 focus:border-emerald-500 focus:outline-none text-white"
                    data-testid="select-budget"
                  >
                    <option value="">Prefer not to say</option>
                    <option value="under_25k">Under $25K</option>
                    <option value="25k_100k">$25K – $100K</option>
                    <option value="100k_500k">$100K – $500K</option>
                    <option value="500k_plus">$500K+</option>
                  </select>
                </Field>

                <Field label="CSR focus areas (select any that apply)">
                  <div className="flex flex-wrap gap-2">
                    {FOCUS_AREAS.map((area) => (
                      <button
                        type="button"
                        key={area}
                        onClick={() => toggleFocus(area)}
                        className={`px-3 py-2 rounded-md text-sm border transition ${
                          form.focus_areas.includes(area)
                            ? "bg-emerald-500 text-black border-emerald-500 font-semibold"
                            : "bg-black text-zinc-300 border-zinc-800 hover:border-emerald-700"
                        }`}
                        data-testid={`focus-${area.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Anything we should know? (optional)">
                  <Textarea
                    rows={4}
                    placeholder="Specific causes, geographic priorities, timing, etc."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="bg-black border-zinc-800 focus:border-emerald-500"
                    data-testid="input-message"
                  />
                </Field>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold h-14 text-base"
                  data-testid="submit-inquiry-btn"
                >
                  {submitting ? "Sending..." : "Request partnership briefing"}
                </Button>
                <p className="text-xs text-zinc-500 text-center">
                  We respond within 1 business day. No marketing spam — ever.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-sm text-zinc-500">
          <div>
            <div className="flex items-center gap-2 text-white mb-1">
              <Lightning weight="fill" className="text-emerald-500 w-5 h-5" />
              <span className="font-bold">FlashGrants</span>
            </div>
            <p>flash-grants.com — Built for South Carolina</p>
          </div>
          {refLabel && (
            <p className="text-zinc-400" data-testid="footer-referrer">
              Trusted partner: <span className="text-emerald-500 font-semibold">{refLabel}</span>
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ number, label }) => (
  <div>
    <p className="text-4xl font-black text-emerald-500">{number}</p>
    <p className="text-sm text-zinc-400 mt-1">{label}</p>
  </div>
);

const ProblemCard = ({ icon, title, body }) => (
  <div className="bg-black border border-zinc-800 rounded-lg p-6">
    <div className="mb-4">{icon}</div>
    <h3 className="text-lg font-bold mb-2">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-zinc-300 mb-2">{label}</label>
    {children}
  </div>
);
